import { useState, useMemo, useEffect, useCallback } from 'react';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import { getTableColumns, ReportLevel } from "./utils/tableUtils";
import { createExportHandlers } from "./utils/exportUtils";
import TooltipOption from '../../../../core/common/tooltipOption';
import ErrorModal from '../../../../core/common/ErrorModal';
import SuccessModal from '../../../../core/common/successModal';
import {
  stateMealData,
  districtMealData,
  blockMealData,
  villageMealData,
  VillageMealData
} from './mealReportData';
import { RootState } from '../../../../core/data/redux/store';
import { fetchUserRoleApi, fetchReportSummaryApi } from '../../../../core/data/services/reportService';
import { fetchSchoolApi } from '../../../../core/data/services/schoolService';
import { fetchReportsSuccess } from '../../../../core/data/redux/reportSlice';
import { getUserDetails } from '../../../../lib/getUserDetails';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

const StateMealReport = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Hierarchical filters
  const [selectedState, setSelectedState] = useState<string | number>('');
  const [selectedStateName, setSelectedStateName] = useState<string>('');

  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedDistrictName, setSelectedDistrictName] = useState<string>('');

  const [selectedBlock, setSelectedBlock] = useState<string>('');
  const [selectedBlockName, setSelectedBlockName] = useState<string>('');

  const [selectedVillage, setSelectedVillage] = useState('');
  const [selectedVillageName, setSelectedVillageName] = useState<string>('');

  const [schoolsCount, setSchoolsCount] = useState<number>(0);

  const user = useSelector(
    (state: RootState) => state.user.Users
  );

  // Get reports from Redux store
  const reports = useSelector((state: RootState) => state.reports.reports);

  // User role detection functions
  const getUserRole = useCallback(() => {
    let role = user?.[0].role;
    return role;
  }, [user]);

  const isStateAdmin = useCallback(() => getUserRole() === 'state-admin' || getUserRole() === 'state Admin' || getUserRole() === 'State Admin', [getUserRole]);
  const isDistrictAdmin = useCallback(() => getUserRole() === 'district-admin' || getUserRole() === 'district Admin' || getUserRole() === 'District Admin', [getUserRole]);
  const isBlockAdmin = useCallback(() => getUserRole() === 'block-admin' || getUserRole() === 'block Admin' || getUserRole() === 'Block Admin', [getUserRole]);
  const isVillageAdmin = useCallback(() => getUserRole() === 'village-admin' || getUserRole() === 'village Admin' || getUserRole() === 'Village Admin', [getUserRole]);
  const isSuperAdmin = useCallback(() => getUserRole() === 'admin' || getUserRole() === 'Admin' || getUserRole() === 'Super Admin', [getUserRole]);

  // Set initial values based on admin type after component mounts
  useEffect(() => {
    fetchUserRoleApi();

    const userEntity = user?.[0]?.relatedEntity;

    if (userEntity && isStateAdmin()) {
      setSelectedState(userEntity?.states?.id);
    }

    if (userEntity && isDistrictAdmin()) {
      setSelectedDistrict(userEntity?.districts?.id);
      setSelectedState(userEntity?.districts?.stateId);
    }

    if (userEntity && isBlockAdmin()) {
      setSelectedBlock(userEntity?.blockAreas?.id);
      setSelectedDistrict(userEntity?.blockAreas?.districtId);
      setSelectedState(userEntity?.blockAreas?.stateId);
    }
  }, [user, isStateAdmin, isDistrictAdmin, isBlockAdmin]);

  // Date filters - default to today's date
  //const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
  const today = '2025-09-15'; // Format: YYYY-MM-DD
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);

  // Fetch reports with date range
  const fetchReports = useCallback(async (startDate: string, endDate: string) => {
    setIsLoadingReports(true);
    try {
      let payload: any = {
        startDate,
        endDate,
        filter: 'level|state'
      };

      if (selectedVillage) {
        payload.villageId = selectedVillage;
        payload.filter = 'level|school';
      } else if (selectedBlock) {
        payload.blockAreaId = selectedBlock;
        payload.filter = 'level|villageWard';
      } else if (selectedDistrict) {
        payload.districtId = selectedDistrict;
        payload.filter = 'level|blockArea';
      } else if (selectedState) {
        payload.stateId = selectedState;
        payload.filter = 'level|district';
      }

      // Get classSectionIds from user data directly to avoid dependency issues
      const userData = getUserDetails();

      const userSchoolClasses = userData?.[0]?.relatedEntity?.schoolClasses || [];

      const userClassSectionIds = (userSchoolClasses as any[])?.flatMap((item: any) =>
        item?.classSection?.map((section: any) => section?.id) || []
      ) || [];

      let data: any;
      if (!selectedVillage) {
        data = await fetchReportSummaryApi(payload);
      } else {
        data = await fetchSchoolApi({ ...payload, villageWardId: selectedVillage, limit: 1000 });
        data = data?.data || [];
        setSchoolsCount(data?.total || 0);
      }

      dispatch(fetchReportsSuccess([]));

      // Handle different data structures from the API
      let reportsData: any[] = [];
      if (Array.isArray(data)) {
        reportsData = data;
      } else if (data && typeof data === 'object') {
        reportsData = (data as any).studentscount || [];
      }
      dispatch(fetchReportsSuccess(reportsData));
    } catch (error: any) {
      // Handle empty results gracefully
      dispatch(fetchReportsSuccess([]));

      // Only show error for actual API failures, not empty results
      if (error.message !== 'Student not found' && !error.message.includes('not found')) {
        const msg = error?.message ?? "Failed to fetch reports.";
        setError(msg);
        setShowErrorModal(true);
        console.error("Error fetching reports:", msg);
      }
    } finally {
      setIsLoadingReports(false);
    }
  }, [dispatch, selectedState, selectedDistrict, selectedBlock, selectedVillage]);


  // Call fetchReports on component mount with initial dates
  useEffect(() => {
    if (startDate && endDate) {
      fetchReports(startDate, endDate);
    }
  }, []); // Empty dependency array - only run once on mount

  // Call fetchReports when date range changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchReports(startDate, endDate);
    } else {
      console.log('Dates not ready yet:', { startDate, endDate });
    }
  }, [startDate, endDate, fetchReports, selectedState, selectedDistrict, selectedBlock, selectedVillage]);

  // Determine current report level - show the next level to drill down to
  const currentLevel: ReportLevel = useMemo(() => {
    if (selectedState && selectedDistrict && selectedBlock && selectedVillage) return 'school';
    if (selectedState && selectedDistrict && selectedBlock) return 'village';
    if (selectedState && selectedDistrict) return 'block';
    if (selectedState) return 'district';
    return 'state';
  }, [selectedState, selectedDistrict, selectedBlock, selectedVillage]);

  // Get current data based on level
  const currentData = useMemo(() => {
    // If we have API reports data, use that
    if (reports && reports.length > 0) {
      return reports;
    }
    // Fallback to static data if no API data
    switch (currentLevel) {
      case 'state':
        return stateMealData;
      case 'district':
        return districtMealData[selectedState] || [];
      case 'block':
        return blockMealData[selectedDistrict] || [];
      case 'village':
      case 'school':
        return villageMealData[selectedBlock] || [];
      default:
        return stateMealData;
    }
  }, [currentLevel, selectedState, selectedDistrict, selectedBlock, reports]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return currentData;

    return (currentData as any[]).filter((item: any) => {
      const searchField = item.stateUT || item.district || item.block || item.schoolName || '';
      return searchField.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [currentData, searchTerm]);


  // Create export handlers
  const exportHandlers = useMemo(() =>
    createExportHandlers(
      currentLevel,
      reports,
      getTableColumns,
      (message) => {
        setSuccessMessage(message);
        setShowSuccessModal(true);
      },
      (message) => {
        setError(message);
        setShowErrorModal(true);
      }
    ), [currentLevel, filteredData]
  );

  // Handle filter changes
  const handleStateChange = (state: string) => {
    setSelectedState(state);
    // Only clear lower levels if we're clearing the state
    if (!state) {
      setSelectedDistrict('');
      setSelectedBlock('');
      setSelectedVillage('');
      setSelectedVillageName('');
      setSelectedBlockName('');
      setSelectedDistrictName('');
    }
  };

  const handleDistrictChange = (district: string) => {
    setSelectedDistrict(district);
    // Only clear lower levels if we're clearing the district
    if (!district) {
      setSelectedBlock('');
      setSelectedVillage('');
      setSelectedVillageName('');
      setSelectedBlockName('');
      setSelectedDistrict('');
      setSelectedDistrictName('');
    }
  };

  const handleBlockChange = (block: string) => {
    setSelectedBlock(block);
    // Only clear lower levels if we're clearing the block
    if (!block) {
      setSelectedVillage('');
      setSelectedVillageName('');
      setSelectedBlockName('');
      setSelectedBlock('');
    }
  };

  // Handle date range change
  const handleDateRangeChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    console.log('Date range changed:', start, 'to', end);
  };

  // Reset all filters
  const handleResetFilters = () => {
    if (isSuperAdmin()) {
      setSelectedState('');
      setSelectedDistrict('');
      setSelectedBlock('');
      setSelectedVillage('');
    } else if (isStateAdmin()) {
      // For state-admin: Reset to Tamil Nadu district level
      setSelectedState('TAMIL NADU');
      setSelectedDistrict('');
      setSelectedBlock('');
      setSelectedVillage('');
    } else if (isDistrictAdmin()) {
      // For district-admin: Reset to Madurai block level
      setSelectedState('TAMIL NADU');
      setSelectedDistrict('MADURAI');
      setSelectedBlock('');
      setSelectedVillage('');
    } else if (isBlockAdmin()) {
      // For block-admin: Reset to Madurai North village level
      setSelectedState('TAMIL NADU');
      setSelectedDistrict('MADURAI');
      setSelectedBlock('MADURAI NORTH');
      setSelectedVillage('');
    } else {
      // For super-admin: Reset to all states
      setSelectedState('');
      setSelectedDistrict('');
      setSelectedBlock('');
      setSelectedVillage('');
    }
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
  };

  // Get table headers based on current level
  const getTableHeaders = () => {
    const headerStyle = { padding: '12px 4px', fontSize: '12px' };

    let title = 'State / UT';

    if (currentLevel === 'village') {
      title = 'Village';
    } else if (currentLevel === 'district') {
      title = 'District';
    } else if (currentLevel === 'block') {
      title = 'Block';
    } else if (currentLevel === 'school') {
      title = 'School';
    }

    switch (currentLevel) {
      case 'school':
        return (
          <tr>
            <th className="text-center" style={headerStyle}>S No.</th>
            <th className="text-center" style={headerStyle}>UDISE Code</th>
            <th className="text-center" style={headerStyle}>School Name</th>
            <th className="text-center" style={headerStyle}>Category</th>
            <th className="text-center" style={headerStyle}>Type</th>
            <th className="text-center" style={headerStyle}>Enrolment</th>
            <th className="text-center" style={headerStyle}>Holiday</th>
            <th className="text-center" style={headerStyle}>Reporting Status</th>
            <th className="text-center" style={headerStyle}>Meals Served</th>
          </tr>
        );
      default:
        return (
          <>
            <tr>
              <th rowSpan={2} className="text-center align-middle" style={headerStyle}>S. No.</th>
              <th rowSpan={2} className="text-center align-middle" style={headerStyle}>{title}</th>
              <th rowSpan={2} className="text-center align-middle" style={headerStyle}>Total Schools</th>
              <th colSpan={2} className="text-center" style={headerStyle}>School Reporting Status</th>
              <th colSpan={3} className="text-center" style={headerStyle}>Reported Schools Meal Status</th>
              <th colSpan={2} className="text-center" style={headerStyle}>Student Enrolment</th>
              <th colSpan={4} className="text-center" style={headerStyle}>Number of Students in reporting Schools</th>
            </tr>
            <tr>
              <th className="text-center" style={headerStyle}>Reported</th>
              <th className="text-center" style={headerStyle}>Not Reported</th>
              <th className="text-center" style={headerStyle}>Holiday</th>
              <th className="text-center" style={headerStyle}>Meal served (%)</th>
              <th className="text-center" style={headerStyle}>Meal not served %</th>
              <th className="text-center" style={headerStyle}>Student Enrolment</th>
              <th className="text-center" style={headerStyle}>No. of Students approved by PAB</th>
              <th className="text-center" style={headerStyle}>Enrolment</th>
              <th className="text-center" style={headerStyle}>Enrolment %</th>
              <th className="text-center" style={headerStyle}>Availed meals</th>
              <th className="text-center" style={headerStyle}>Availed meals %</th>
            </tr>
          </>
        );
    }
  };

  // Calculate totals for village level
  const villageTotals = useMemo(() => {
    if (currentLevel !== 'school') return { totalEnrolment: 0, totalMealsServed: 0 };

    return (filteredData as VillageMealData[]).reduce((acc, item) => ({
      totalEnrolment: acc.totalEnrolment + (item.enrolment || 0),
      totalMealsServed: acc.totalMealsServed + (item.mealsServed || 0)
    }), { totalEnrolment: 0, totalMealsServed: 0 });
  }, [filteredData, currentLevel]);

  // Render table row based on current level
  const renderTableRow = (item: any, index: number) => {
    const cellStyle = { padding: '10px 4px', fontSize: '12px' };

    switch (currentLevel) {
      case 'school':
        return (
          <tr key={item.serialNo}>
            <td
              className="text-center"
              style={cellStyle}
              title={`Click to view details for ${item.schoolName}`}
            >
              {index + 1}
            </td>
            <td className="text-center" style={{
              fontWeight: 'bold',
              padding: '10px 4px',
              fontSize: '12px'
            }}
            >{item.udiseCode ?? 'NA'}</td>
            <td className="fw-bold" style={cellStyle}>{item.name}</td>
            <td className="text-center" style={cellStyle}>{item.category}</td>
            <td className="text-center" style={cellStyle}>{item.schoolType}</td>
            <td className="text-center" style={cellStyle}>{item.enrolment}</td>
            <td className="text-center" style={cellStyle}>{item.holiday}</td>
            <td className="text-center" style={cellStyle}>{item.reportingStatus}</td>
            <td className="text-center" style={cellStyle}>{item.mealsServed}</td>
          </tr>
        );
      default:
        return (
          <tr key={item.serialNo}>
            <td
              className="text-center"
              style={cellStyle}
              title={`Click to filter by ${item.id}`}
            >
              {item.id}
            </td>
            <td className="fw-bold" style={{
              cursor: 'pointer',
              color: '#007bff',
              textDecoration: 'underline',
              fontWeight: 'bold',
              padding: '10px 4px',
              fontSize: '12px'
            }}
              onClick={() => {
                if (selectedBlock) {
                  setSelectedVillage(item.villageWardId);
                  setSelectedVillageName(item.name);
                } else if (selectedDistrict) {
                  setSelectedBlock(item.blockAreaId);
                  setSelectedBlockName(item.name);
                } else if (selectedState) {
                  setSelectedDistrict(item.districtId);
                  setSelectedDistrictName(item.name);
                } else {
                  setSelectedState(item.stateId);
                  setSelectedStateName(item.name);
                }
              }}>{item?.name}</td>
            <td className="text-center" style={cellStyle}>{item.totalSchools ?? 0}</td>
            <td className="text-center" style={cellStyle}>
              {item?.schoolsReported ?? 0} [{item?.schoolsReportedPct}%]
            </td>
            <td className="text-center" style={cellStyle}>
              {item?.schoolsNotReported ?? 0} [{item?.schoolsNotReportedPct}%]
            </td>
            <td className="text-center" style={cellStyle}>{item?.holiday ?? 0}</td>
            <td className="text-center" style={cellStyle}>{item?.mealsServedPct}%</td>
            <td className="text-center" style={cellStyle}>{item?.mealsNotServedPct}%</td>
            <td className="text-center" style={cellStyle}>{item?.totalStudents ?? 0}</td>
            <td className="text-center" style={cellStyle}>{item?.mdmEnrolledCount ?? 0}</td>
            <td className="text-center" style={cellStyle}>{item?.reportingSchoolsEnrolment?.toLocaleString()}</td>
            <td className="text-center" style={cellStyle}>{item?.mdmEnrolledPct}%</td>
            <td className="text-center" style={cellStyle}>{item?.melasServedCount?.toLocaleString()}</td>
            <td className="text-center" style={cellStyle}>{item?.availedMealPct}%</td>
          </tr>
        );
    }
  };

  // Render TOTAL row for village level
  const renderTotalRow = () => {
    if (currentLevel !== 'school') return null;

    const cellStyle = { padding: '10px 4px', fontSize: '12px' };

    return (
      <tr className="table-secondary">
        <td colSpan={3} className="fw-bold text-center" style={cellStyle}>TOTAL</td>
        <td style={cellStyle}></td>
        <td style={cellStyle}></td>
        <td className="text-center fw-bold" style={cellStyle}>{villageTotals.totalEnrolment}</td>
        <td style={cellStyle}></td>
        <td style={cellStyle}></td>
        <td className="text-center fw-bold" style={cellStyle}>{villageTotals.totalMealsServed}</td>
      </tr>
    );
  };

  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="content">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">
                  {isStateAdmin() ? `${selectedState} - Hierarchical Meal Report` :
                    isDistrictAdmin() ? `${selectedDistrict} - Hierarchical Meal Report` :
                      isBlockAdmin() ? `${selectedBlock} - Hierarchical Meal Report` :
                        'Hierarchical Meal Report'}
                </h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Reports</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {isStateAdmin() ? `${selectedState} - Hierarchical Meal Report` :
                        isDistrictAdmin() ? `${selectedDistrict} - Hierarchical Meal Report` :
                          isBlockAdmin() ? `${selectedBlock} - Hierarchical Meal Report` :
                            'Hierarchical Meal Report'}
                    </li>
                  </ol>
                </nav>
              </div>

              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <TooltipOption
                  onExportPDF={exportHandlers.onExportPDF}
                  onExportExcel={exportHandlers.onExportExcel}
                />
              </div>
            </div>

            {/* Date Filter */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-9">
                    {startDate && endDate && (
                      <div>
                        <i className="ti ti-calendar me-2"></i>
                        <span className="text-muted">
                          Showing data from <b>{startDate}</b> to <b>{endDate}</b>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="col-md-3 text-end">
                    <DatePicker
                      value={dayjs(startDate)}
                      onChange={(date: any) => {
                        if (date) {
                          setStartDate(date.format('YYYY-MM-DD'));
                          setEndDate(date.format('YYYY-MM-DD'));
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <BreadcrumbNavigation
              isDistrictAdmin={isDistrictAdmin}
              isBlockAdmin={isBlockAdmin}
              isStateAdmin={isStateAdmin}
              selectedState={selectedState}
              selectedDistrict={selectedDistrict}
              selectedBlock={selectedBlock}
              selectedVillage={selectedVillage}
              onResetFilters={handleResetFilters}
              onDistrictChange={handleDistrictChange}
              onBlockChange={handleBlockChange}
              onStateChange={handleStateChange}
              selectedStateName={selectedStateName}
              selectedDistrictName={selectedDistrictName}
              selectedBlockName={selectedBlockName}
              selectedVillageName={selectedVillageName}
            />

            {/* Report Table */}
            <div className="card">
              <div className="card-body p-0">
                {/* Loading Indicator */}
                {isLoadingReports && (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 mb-0">Loading reports...</p>
                  </div>
                )}

                {/* Table Content */}
                {!isLoadingReports && (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover mb-0" style={{ fontSize: '12px' }}>
                      <thead className="table-dark">
                        {getTableHeaders()}
                      </thead>
                      <tbody>
                        {reports?.map((item: any, index: number) => renderTableRow(item, index))}
                        {renderTotalRow()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      <ErrorModal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </>
  );
};

export default StateMealReport;